import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import SearchableSelect from "@/components/SearchableSelect";
import ListSearchBar, { LoadMore } from "@/components/ListSearchBar";
import usePagedSearch, { useDebounced } from "@/hooks/usePagedSearch";
import { fmtDate } from "@/lib/utils";
import { EXPENSE_STATUSES, PAYMENT_METHODS, expenseStatus, paymentMethodLabel } from "@/constants/outsidePurchases";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const filterCls = "h-10 px-3 rounded-lg border border-slate-200 text-[13px] min-w-[150px]";

// Meeting 3 — the Expense Log, laid out like the client's Smartsheet. Search
// and filters run on the server across every entry (not just what's on
// screen); "Waiting on me" is where an approver approves, under Procurement.
export default function ExpenseLog() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(null); // null until we know if anything waits on me
  const [mine, setMine] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [team, setTeam] = useState([]);
  const [search, setSearch] = useState("");
  const [purchasedBy, setPurchasedBy] = useState("");
  const [jobId, setJobId] = useState("");
  const [payment, setPayment] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const q = useDebounced(search);

  useEffect(() => {
    api.get("/contractor/jobs").then((r) => setJobs(r.data));
    api.get("/contractor/team").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/contractor/outside-purchases/approvals").then((r) => {
      setMine(r.data);
      setTab((t) => t ?? (r.data.length > 0 ? "mine" : ""));
    }).catch(() => setTab((t) => t ?? ""));
  }, []);

  const current = tab ?? "";
  const list = usePagedSearch("/contractor/outside-purchases/search", {
    q, status: current === "mine" ? "" : current, purchased_by: purchasedBy, job_id: jobId,
    payment_method: payment, date_from: dateFrom, date_to: dateTo,
  });
  const counts = list.counts || {};
  const rows = current === "mine" ? mine : list.items || [];
  const jobOf = (op) => jobs.find((j) => j.id === (op.job_id || op.context?.job_id));
  const jobCode = (op) => jobOf(op)?.code;
  const jobName = (op) => jobOf(op)?.name;
  const approvedBy = (op) => (expenseStatus(op) === "approved" ? op.approved_by_name : expenseStatus(op) === "pending_approval" ? "Not approved" : "—");
  const filtered = Boolean(q || purchasedBy || jobId || payment || dateFrom || dateTo);

  const tabs = [
    ...(mine.length > 0 ? [{ value: "mine", label: `Waiting on me (${mine.length})` }] : []),
    { value: "", label: `All${counts.all != null ? ` (${counts.all.toLocaleString()})` : ""}` },
    ...EXPENSE_STATUSES.map((s) => ({ ...s, label: `${s.label}${counts[s.value] ? ` (${counts[s.value].toLocaleString()})` : ""}` })),
  ];

  return (
    <div className="p-6 md:p-8" data-testid="expense-log-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Expense Log</h1>
          <p className="text-slate-500 text-sm mt-1">Every purchase made outside a PO — walk-ins, card buys — gets logged here.</p>
        </div>
        <button onClick={() => navigate("/contractor/expense-log/new")} data-testid="new-expense-button" className="w-full sm:w-auto flex items-center justify-center gap-2 h-12 sm:h-auto px-5 sm:py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13.5px] font-bold">
          <Plus size={15} /> New Expense
        </button>
      </div>

      <ListSearchBar value={search} onChange={(v) => { setSearch(v); if (current === "mine") setTab(""); }} placeholder="Search PO #, vendor, vendor invoice #, memo, purchaser…" testId="expense-search">
        <SearchableSelect value={purchasedBy} onChange={(e) => setPurchasedBy(e.target.value)} className={filterCls} data-testid="expense-filter-purchaser">
          <option value="">All purchasers</option>
          {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </SearchableSelect>
        <SearchableSelect value={jobId} onChange={(e) => setJobId(e.target.value)} className={filterCls} data-testid="expense-filter-job">
          <option value="">All jobs</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.code} — {j.name}</option>)}
        </SearchableSelect>
        <SearchableSelect value={payment} onChange={(e) => setPayment(e.target.value)} className={filterCls} data-testid="expense-filter-payment">
          <option value="">Any payment</option>
          {PAYMENT_METHODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </SearchableSelect>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="From" className="h-10 px-3 rounded-lg border border-slate-200 text-[13px]" data-testid="expense-filter-from" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="To" className="h-10 px-3 rounded-lg border border-slate-200 text-[13px]" data-testid="expense-filter-to" />
        {filtered && (
          <button onClick={() => { setSearch(""); setPurchasedBy(""); setJobId(""); setPayment(""); setDateFrom(""); setDateTo(""); }} className="h-10 px-3 text-[12.5px] font-semibold text-sky-600">Clear filters</button>
        )}
      </ListSearchBar>

      <div className="flex gap-2 mt-4 flex-wrap">
        {tabs.map((t) => (
          <button key={t.value || "all"} onClick={() => setTab(t.value)} data-testid={`filter-${t.value || "all"}`}
            className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium ${current === t.value ? (t.value === "mine" ? "bg-amber-500 text-white" : "bg-slate-900 text-white") : t.value === "mine" ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {list.items === null && current !== "mine" ? (
        <div className="py-10 text-center text-slate-400 text-sm">Loading…</div>
      ) : (
        <>
          <div className="sm:hidden mt-5 space-y-2" data-testid="expense-card-list">
            {rows.map((op) => (
              <div key={op.id} onClick={() => navigate(`/contractor/expense-log/${op.id}`)} data-testid={`expense-card-${op.op_number}`} className="border border-slate-200 rounded-xl p-3.5 active:bg-slate-50">
                <div className="flex items-center justify-between gap-2">
                  <span className="mono text-sky-600 font-semibold text-[13px] truncate">{op.po_number || op.op_number}</span>
                  <StatusBadge status={expenseStatus(op)} />
                </div>
                <div className="mt-1.5 text-[14px] font-medium text-slate-800 truncate">{op.vendor_name_freetext || "—"}</div>
                <div className="text-[12px] text-slate-500 truncate">{op.purchased_by_name}{jobCode(op) ? ` · ${jobCode(op)} — ${jobName(op)}` : ""}</div>
                <div className="mt-2 flex items-center justify-between text-[12.5px]">
                  <span className="text-slate-500">{op.purchase_date ? fmtDate(op.purchase_date) : "—"}</span>
                  <span className="mono font-bold shrink-0">{fmt(op.total)}</span>
                </div>
              </div>
            ))}
            {rows.length === 0 && <div className="py-10 text-center text-slate-400 text-sm">{filtered ? "No expenses match." : "No expenses here."}</div>}
          </div>

          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full mt-5 text-[13px]" data-testid="expense-table">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
                  <th className="text-left py-2.5">PO #</th>
                  <th className="text-left py-2.5">Date</th>
                  <th className="text-left py-2.5">Purchaser</th>
                  <th className="text-left py-2.5">Vendor</th>
                  <th className="text-left py-2.5">Vendor Inv #</th>
                  <th className="text-left py-2.5">Job #</th>
                  <th className="text-left py-2.5">Job name</th>
                  <th className="text-left py-2.5">Card / Accounts Payable</th>
                  <th className="text-right py-2.5">Actual</th>
                  <th className="text-left py-2.5 pl-3">Status</th>
                  <th className="text-left py-2.5 pl-3">Approved by</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((op) => (
                  <tr key={op.id} onClick={() => navigate(`/contractor/expense-log/${op.id}`)} className="cursor-pointer hover:bg-slate-50 border-b border-slate-50" data-testid={`expense-row-${op.op_number}`}>
                    <td className="py-3 mono text-sky-600 font-medium whitespace-nowrap">{op.po_number || op.op_number}</td>
                    <td className="py-3 text-slate-500 whitespace-nowrap">{op.purchase_date ? fmtDate(op.purchase_date) : "—"}</td>
                    <td className="py-3">{op.purchased_by_name}</td>
                    <td className="py-3">{op.vendor_name_freetext || "—"}</td>
                    <td className="py-3 text-slate-500 mono">{op.vendor_invoice_number || "—"}</td>
                    <td className="py-3 text-slate-500">{jobCode(op) || "—"}</td>
                    <td className="py-3" data-testid={`expense-job-name-${op.op_number}`}>{jobName(op) || "—"}</td>
                    <td className="py-3 text-slate-500">{op.payment_method ? paymentMethodLabel(op.payment_method) : "—"}</td>
                    <td className="py-3 text-right mono font-medium">{fmt(op.total)}</td>
                    <td className="py-3 pl-3"><StatusBadge status={expenseStatus(op)} /></td>
                    <td className="py-3 pl-3 text-slate-500 text-[12.5px]">{approvedBy(op)}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={11} className="py-10 text-center text-slate-400 text-sm">{filtered ? "No expenses match." : "No expenses here."}</td></tr>}
              </tbody>
            </table>
          </div>
          {current !== "mine" && (
            <LoadMore items={list.items} nextCursor={list.nextCursor} loadMore={list.loadMore} loadingMore={list.loadingMore}
              total={current ? counts[current] : counts.all} />
          )}
        </>
      )}
    </div>
  );
}
